import { useMemo } from 'react';

import { VisibilityState } from '@tanstack/react-table';

import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { DocWithFacts } from '@/api/types/queries';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { PopoverContent } from '@/new/components/shadcn/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

interface ColumnConfigPopoverProps {
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  onClose?: () => void;
  docs?: DocWithFacts[];
  relativityFields?: FilterField[];
  wexlerFields?: FilterField[];
}

const STANDARD_COLUMNS = [
  // Checked by default
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'file.extension',
    label: 'Extension',
  },
  {
    id: 'issues',
    label: 'Issues',
  },
  {
    id: 'persons',
    label: 'Entities',
  },
  {
    id: 'date',
    label: 'Document Date',
  },
  // Unchecked by default
  {
    id: 'author',
    label: 'Author',
  },
  {
    id: 'factsCount',
    label: 'Facts Count',
  },
  {
    id: 'file.createdDate',
    label: 'Processed Date/Time',
  },
];

export function ColumnConfigPopover({
  columnVisibility,
  setColumnVisibility,
  onClose,
  docs,
  relativityFields,
  wexlerFields,
}: ColumnConfigPopoverProps) {
  const handleToggleColumn = (columnId: string) => {
    setColumnVisibility({
      ...columnVisibility,
      // Explicitly set true (show) or false (hide)
      // undefined = visible by default, so if currently visible (undefined or true), hide it
      [columnId]: columnVisibility[columnId] === false ? true : false,
    });
  };

  // Generate dynamic Wexler columns from matter metadataFields (shown in Standard tab)
  const dynamicWexlerColumns = useMemo(() => {
    if (!wexlerFields?.length) return [];
    return wexlerFields.map((field) => ({
      id: `file.fileMetadata.wexlerFields.${field.fieldName}`,
      label: field.displayName,
      isWexler: true,
    }));
  }, [wexlerFields]);

  // Generate dynamic Relativity columns from field mappings
  const dynamicRelativityColumns = useMemo(() => {
    if (!relativityFields?.length) return [];
    return relativityFields.map((field) => ({
      id: `file.fileMetadata.relativityFields.${field.fieldName}`,
      label: field.displayName,
    }));
  }, [relativityFields]);

  // Check if any documents have file metadata with relativity fields AND we have fields to show
  const hasRelativityMetadata =
    (docs?.some(
      (doc) =>
        doc.file?.fileMetadata?.relativityFields && Object.keys(doc.file.fileMetadata.relativityFields).length > 0,
    ) ??
      false) &&
    dynamicRelativityColumns.length > 0;

  // Standard columns content (includes Wexler metadata fields)
  const standardColumnsContent = (
    <div className="space-y-2.5">
      {STANDARD_COLUMNS.map((column) => (
        <label key={column.id} htmlFor={column.id} className="flex cursor-pointer items-center gap-2.5">
          <Checkbox
            id={column.id}
            checked={columnVisibility[column.id] !== false}
            onCheckedChange={() => handleToggleColumn(column.id)}
          />
          <span className="text-sm font-medium text-gray-700">{column.label}</span>
        </label>
      ))}
      {/* Wexler metadata fields - shown in Standard tab */}
      {dynamicWexlerColumns.length > 0 && (
        <>
          <div className="my-3 border-t border-gray-200" />
          {dynamicWexlerColumns.map((column) => (
            <label key={column.id} htmlFor={column.id} className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                id={column.id}
                checked={columnVisibility[column.id] !== false}
                onCheckedChange={() => handleToggleColumn(column.id)}
              />
              <span className="text-sm font-medium text-gray-700">{column.label}</span>
            </label>
          ))}
        </>
      )}
    </div>
  );

  // Relativity columns content
  const relativityColumnsContent = (
    <div className="space-y-2.5">
      {dynamicRelativityColumns.map((column) => (
        <label key={column.id} htmlFor={column.id} className="flex cursor-pointer items-center gap-2.5">
          <Checkbox
            id={column.id}
            checked={columnVisibility[column.id] !== false}
            onCheckedChange={() => handleToggleColumn(column.id)}
          />
          <span className="text-sm font-medium text-gray-700">{column.label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <PopoverContent side="left" className="mt-12 w-96" sideOffset={8}>
      {hasRelativityMetadata ? (
        // Tabbed layout when Relativity columns exist
        <Tabs defaultValue="standard" className="w-full">
          <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 px-4 pt-4">
            <TabsList className="w-full justify-start bg-transparent">
              <TabsTrigger value="standard" className="flex items-center gap-1.5">
                <Icon className="lucide--columns-3 h-3.5 w-3.5 text-gray-600" />
                Standard
              </TabsTrigger>
              <TabsTrigger value="relativity" className="flex items-center gap-1.5">
                <Icon className="lucide--database h-3.5 w-3.5 text-purple-600" />
                Relativity
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="standard" className="mt-4 max-h-96 overflow-y-auto">
            {standardColumnsContent}
          </TabsContent>

          <TabsContent value="relativity" className="mt-4 max-h-96 overflow-y-auto">
            {relativityColumnsContent}
          </TabsContent>
        </Tabs>
      ) : (
        // Flat layout when no Relativity columns
        <>
          <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 p-4">
            <div className="flex items-center gap-2">
              <Icon className="lucide--columns-3 h-4 w-4 text-gray-600" />
              <span className="font-medium text-gray-700">Edit Columns</span>
            </div>
          </div>
          <div className="mt-4 max-h-96 overflow-y-auto">{standardColumnsContent}</div>
        </>
      )}

      <div className="mt-4 flex w-full justify-end border-t pt-4">
        <Button onClick={onClose} size="sm">
          <Icon className="lucide--check h-3.5 w-3.5" />
          <span>Done</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
